import { ErrorModal, TreeNode, TreePickerControlV2 } from "@dexteel/mesf-core";
import {
	CircularProgress,
	FormControl,
	FormHelperText,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayCode, TreeCode } from "../models/DelayCode";
import { getDelayCodes } from "../repositories/DelayCodeRepository";

const buildTreeCodes = (nodes: TreeCode[]): TreeNode => {
	//tree build functions
	const idMapping = nodes.reduce((acc: any, el: TreeCode, i: number) => {
		// console.log(acc)
		acc[el.id] = i;
		return acc;
	}, {});
	let root;
	nodes.forEach((el: TreeCode) => {
		// Handle the root element
		if (el.parentId === null) {
			root = el;
			return;
		}
		// Use our mapping to locate the parent element in our data array
		const parentEl = nodes[idMapping[el.parentId]];
		// Add our current el to its parent's `children` array
		if (parentEl) {
			parentEl.children = [...(parentEl?.children || []), el];
		}
	});
	// @ts-ignore
	return root;
};

const useDelayCodes = (
	assetId: number | null,
	onError?: (error: Error) => void,
) => {
	return useQuery<DelayCode[], Error>({
		queryKey: ["delayCodes", assetId],
		queryFn: ({ signal }) => getDelayCodes({ assetId }, signal),
		onError: onError,
	});
};

export const DelayCodeTreePicker = ({
	label = "Code",
	value,
	onChange,
	disabled,
	assetId,
	fieldError,
	showFullPath = false,
}: {
	label?: String;
	value: number | null;
	onChange: (value: number | null, defaultCategoryId: number | null) => void;
	disabled?: boolean;
	assetId: number | null;
	fieldError?: FieldError | undefined;
	showFullPath?: boolean;
}) => {
	const [error, setError] = useState("");

	const { data, isLoading } = useDelayCodes(assetId, (error) =>
		setError(error.message),
	);

	const rowsMapped = (Array.isArray(data) ? data : []).map(
		(del: DelayCode) =>
			({
				id: del.DelayCodeId,
				parentId: del.ParentDelayCodeId,
				name: del.DelayCodeName,
				isLeaf: del.IsLeaf,
				isActive: true,
				isSelectable: del.IsSelectable,
				children: [],
				sortOrder: del.SortOrder,
			}) as TreeCode,
	);

	const delayCodes = buildTreeCodes(rowsMapped || []);

	if (isLoading || delayCodes == null) {
		return (
			<TextField
				disabled
				label="Code"
				InputProps={{
					endAdornment: isLoading && (
						<CircularProgress color="inherit" size={20} />
					),
				}}
			/>
		);
	} else {
		return (
			<>
				<FormControl
					error={!!fieldError}
					fullWidth
					sx={{
						"& > .MuiGrid2-root": {
							backgroundColor: "transparent",
						},
						"& .MuiOutlinedInput-root": {
							backgroundColor: "background.paper",
						},
					}}
				>
					<TreePickerControlV2
						inputTitle={label}
						title={label}
						dataSource={delayCodes}
						value={value || 0}
						onSelect={(value: number) => {
							const defaultCategoryId = (Array.isArray(data) ? data : []).find(
								(c) => c.DelayCodeId === value,
							)?.DelayCategoryId;
							onChange(value, defaultCategoryId ?? null);
						}}
						selectBranch
						selectActive={false}
						selectInternal={false}
						disabled={disabled}
						showClearButton
						showFullPath={showFullPath}
						mostUsedCount={5}
						allowSearch
						itemsWithParent={[
							"F1",
							"F2",
							"F3",
							"F4",
							"F5",
							"F6",
							"F7",
							"R1",
							"R2",
							"R3",
							"R4",
						]}
						dialogMaxWidth="md"
					/>
					{fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
				</FormControl>
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		);
	}
};
